import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const CONTACT_EMAIL = 'a.capelas@ambitustrategy.com';

const initialForm = {
  name: '',
  email: '',
  company: '',
  message: '',
};

export function createContactMailto(form, copy) {
  const subject = copy.subject.replace('{{name}}', form.name);
  const body = [
    `${copy.name}: ${form.name}`,
    `${copy.email}: ${form.email}`,
    form.company ? `${copy.company}: ${form.company}` : null,
    '',
    `${copy.message}:`,
    form.message,
  ].filter((line) => line !== null).join('\n');

  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function openEmailClient(mailtoUrl) {
  window.location.href = mailtoUrl;
}

export default function ContactForm({ onComposeEmail = openEmailClient }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
    setStatus('idle');
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) nextErrors.name = t('contact.validation.required');
    if (!form.email.trim()) {
      nextErrors.email = t('contact.validation.required');
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      nextErrors.email = t('contact.validation.email');
    }
    if (!form.message.trim()) nextErrors.message = t('contact.validation.required');

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus('error');
      return;
    }

    const mailtoUrl = createContactMailto(form, {
      subject: t('contact.emailContent.subject'),
      name: t('contact.name'),
      email: t('contact.email'),
      company: t('contact.company'),
      message: t('contact.message'),
    });

    try {
      onComposeEmail(mailtoUrl);
      setStatus('prepared');
    } catch {
      setStatus('compose-error');
    }
  };

  return (
    <section className="contact-section" id="Contact" aria-labelledby="contact-title">
      <div className="section-container contact-layout">
        <div className="contact-intro">
          <p className="section-eyebrow">{t('contact.eyebrow')}</p>
          <h2 id="contact-title">{t('contact.title')}</h2>
          <p className="contact-intro__description">{t('contact.introduction')}</p>

          <div className="contact-direct">
            <span>{t('contact.directLabel')}</span>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </div>
        </div>

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          <h3>{t('contact.formTitle')}</h3>

          <div className="contact-form__row">
            <div className="contact-field">
              <label htmlFor="contact-name">{t('contact.name')}</label>
              <input
                id="contact-name"
                name="name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={updateField}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'contact-name-error' : undefined}
              />
              {errors.name && <span className="contact-field__error" id="contact-name-error">{errors.name}</span>}
            </div>

            <div className="contact-field">
              <label htmlFor="contact-email">{t('contact.email')}</label>
              <input
                id="contact-email"
                name="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={updateField}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'contact-email-error' : undefined}
              />
              {errors.email && <span className="contact-field__error" id="contact-email-error">{errors.email}</span>}
            </div>
          </div>

          <div className="contact-field">
            <label htmlFor="contact-company">{t('contact.company')} <span>{t('contact.optional')}</span></label>
            <input
              id="contact-company"
              name="company"
              type="text"
              autoComplete="organization"
              value={form.company}
              onChange={updateField}
            />
          </div>

          <div className="contact-field">
            <label htmlFor="contact-message">{t('contact.message')}</label>
            <textarea
              id="contact-message"
              name="message"
              rows="5"
              value={form.message}
              onChange={updateField}
              placeholder={t('contact.messagePlaceholder')}
              aria-invalid={Boolean(errors.message)}
              aria-describedby={errors.message ? 'contact-message-error' : undefined}
            />
            {errors.message && <span className="contact-field__error" id="contact-message-error">{errors.message}</span>}
          </div>

          <button className="contact-submit" type="submit">{t('contact.submit')}</button>
          <p className="contact-form__note">{t('contact.privacyNote')}</p>

          <div className="contact-form__status" aria-live="polite">
            {status === 'error' && <p className="is-error" role="alert">{t('contact.validation.summary')}</p>}
            {status === 'compose-error' && <p className="is-error" role="alert">{t('contact.composeError')}</p>}
            {status === 'prepared' && <p className="is-prepared">{t('contact.prepared')}</p>}
          </div>
        </form>
      </div>
    </section>
  );
}
